/**
 * Day 84 P6.3 — OverrideEditModal
 *
 * Modal for editing manual sale price + velocity overrides per
 * (product, path). Mirrors Day 53b BrandMergeModal sibling-modal
 * shape (backdrop + dialog + footer button row). Save handler covers four
 * surfaces per L11 + R12:
 *
 *   - Sale price INSERT (no existing price_overrides row, new value)
 *   - Sale price UPDATE (existing row, value changed; smart-spread provenance)
 *   - Velocity INSERT / UPDATE (same shape on velocity_overrides)
 *   - Per-field Remove buttons (ConfirmDialog-gated DELETE)
 *
 * Validation per L15 — empty input = no-op for that field; non-finite or
 * non-positive sale price + negative velocity → toast + bail. Reason
 * textarea is shared (per L11) and stamped into both provenance envelopes
 * when both fields change in a single save.
 *
 * Auto-focus the first input via useRef + useEffect (Settings.tsx
 * InlineParamRow precedent — avoids jsx-a11y/no-autofocus).
 */

import { useEffect, useRef, useState, type JSX } from 'react';
import { supabase } from '../../lib/supabase';
import { translatePostgrestError } from '../../lib/translatePostgrestError';
import { extractEnvelope } from '../../lib/extractEnvelope';
import { PROVENANCE_SOURCE_MANUAL } from '../../lib/provenanceSources';
import ConfirmDialog from '../ui/ConfirmDialog';
import type { ToastState } from '../ui/Toast';
import type { VelocityOverrideRow, PriceOverrideRow } from '../../hooks/useCatalog';
import type { UserError } from '../../lib/errorCodes';
import { FieldError } from '../ui/FieldError';

interface OverrideEditModalProps {
  open: boolean;
  productId: string;
  /** fpc path_key — feeds the override tables' enum-typed `marketplace` column
   *  (the stored name renames only when the enum dies, slice-B family). */
  path: string;
  pathLabel: string;
  existingVelocity: VelocityOverrideRow | null;
  existingPrice: PriceOverrideRow | null;
  userId: string | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  showToast: (t: ToastState) => void;
}

type ConfirmTarget = 'velocity' | 'price' | null;

export default function OverrideEditModal({
  open,
  productId,
  path,
  pathLabel,
  existingVelocity,
  existingPrice,
  userId,
  onClose,
  onSaved,
  showToast,
}: OverrideEditModalProps): JSX.Element | null {
  const [salePriceInput, setSalePriceInput] = useState<string>('');
  const [velocityInput, setVelocityInput] = useState<string>('');
  const [reasonInput, setReasonInput] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [confirmRemove, setConfirmRemove] = useState<ConfirmTarget>(null);
  const [removing, setRemoving] = useState<boolean>(false);
  // Day 84.6.5 P3a.4 + P3b — serverFieldErrors state plumbing per §21
  // setFieldErrors per-page-local-state convention. FieldError JSX composition
  // at price + velocity inputs lands below per Phase 3b. Composite UNIQUE
  // collisions on price_overrides_product_id_marketplace_key /
  // velocity_overrides_product_id_marketplace_key route to toast (translator
  // returns field_path: null per KNOWN_COMPOSITE_CONSTRAINTS registry); these
  // FieldError sites render null per §21 empty-match contract — no-op safety
  // net for composite case, fires for single-field validation errors (e.g.,
  // 23514 CHECK on negative price returning field_path: 'sale_price').
  const [serverFieldErrors, setServerFieldErrors] = useState<UserError[]>([]);

  const firstInputRef = useRef<HTMLInputElement | null>(null);

  // Reset inputs whenever the modal opens (or the underlying override rows change
  // between opens). On initial open with existing rows, pre-fill the inputs.
  useEffect(() => {
    if (!open) return;
    setSalePriceInput(existingPrice?.sale_price != null ? String(existingPrice.sale_price) : '');
    setVelocityInput(
      existingVelocity?.monthly_sales != null ? String(existingVelocity.monthly_sales) : ''
    );
    setReasonInput('');
    setConfirmRemove(null);
    setServerFieldErrors([]);
  }, [open, existingPrice, existingVelocity]);

  useEffect(() => {
    if (open && firstInputRef.current) firstInputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !saving && !removing && !confirmRemove) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, removing, confirmRemove, onClose]);

  if (!open) return null;

  async function handleSave(): Promise<void> {
    const trimmedPrice = salePriceInput.trim();
    const trimmedVelocity = velocityInput.trim();
    const reason = reasonInput.trim() || null;

    // L15 validation — empty means "no change for this field" not "clear it"
    // (clearing requires the Remove button + ConfirmDialog gate).
    let parsedPrice: number | null = null;
    if (trimmedPrice !== '') {
      const n = Number(trimmedPrice);
      if (!Number.isFinite(n) || n <= 0) {
        showToast({ kind: 'error', message: 'Sale price must be a positive number' });
        return;
      }
      parsedPrice = n;
    }

    let parsedVelocity: number | null = null;
    if (trimmedVelocity !== '') {
      const n = Number(trimmedVelocity);
      if (!Number.isFinite(n) || n < 0) {
        showToast({ kind: 'error', message: 'Velocity must be a non-negative number' });
        return;
      }
      parsedVelocity = n;
    }

    // No-op case — both inputs empty AND no existing rows AND no changes intended.
    if (parsedPrice === null && parsedVelocity === null) {
      showToast({ kind: 'info', message: 'No changes to save' });
      return;
    }

    setSaving(true);
    setServerFieldErrors([]);

    // Price branch — INSERT or UPDATE on price_overrides.
    if (parsedPrice !== null) {
      const changed = !existingPrice || existingPrice.sale_price !== parsedPrice;
      if (changed) {
        const existingProv = (existingPrice?.provenance ?? {}) as Record<string, unknown>;
        const newProv = {
          ...existingProv,
          sale_price: { source: PROVENANCE_SOURCE_MANUAL, reason },
        };
        if (existingPrice) {
          // U4 Slice D (R1B-F12) — direct UPDATE on price_overrides is REVOKEd; the edit routes
          // through the update_price_override CAS RPC (expected sale_price → STALE_CONFLICT on a
          // concurrent change, never a silent LWW). One atomic UPDATE preserves the provenance trigger.
          const env = extractEnvelope<{ id: string }>(
            await supabase.rpc('update_price_override', {
              p_id: existingPrice.id,
              p_expected_sale_price: existingPrice.sale_price,
              p_new_sale_price: parsedPrice,
              p_notes: reason,
              p_set_by: userId,
              p_provenance: newProv,
            } as never)
          );
          if (!env.isOk) {
            setSaving(false);
            const ue = env.userErrors[0];
            if (ue?.field_path != null) {
              setServerFieldErrors([ue]);
            } else {
              showToast({
                kind: 'error',
                message: `Price update failed — ${ue?.message ?? 'reload and retry'}`,
              });
            }
            return;
          }
        } else {
          // U2 S4 (Approach F): price_overrides is variant-keyed. Resolve the product's variant and
          // fail loud on multiplicity — mirrors the server-side upsert guard; the variant-keyed
          // override UI (per-condition) is owed at U2 S6.
          const { data: variantRows, error: variantErr } = await supabase
            .from('variants')
            .select('id')
            .eq('product_id', productId);
          if (variantErr || !variantRows || variantRows.length !== 1) {
            setSaving(false);
            showToast({
              kind: 'error',
              message: variantErr
                ? `Price insert failed — ${translatePostgrestError(variantErr).message}`
                : `Price insert failed — expected exactly one variant for this product (found ${variantRows?.length ?? 0}); the variant-keyed override UI is owed at U2 S6.`,
            });
            return;
          }
          const { error } = await supabase.from('price_overrides').insert({
            variant_id: variantRows[0].id,
            // marketplace = the enum-typed stored column (value is an fpc path key).
            marketplace: path,
            sale_price: parsedPrice,
            notes: reason,
            set_by: userId,
            provenance: newProv,
          } as never);
          if (error) {
            setSaving(false);
            const ue = translatePostgrestError(error);
            if (ue.field_path !== null) {
              setServerFieldErrors([ue]);
            } else {
              showToast({ kind: 'error', message: `Price insert failed — ${ue.message}` });
            }
            return;
          }
        }
      }
    }

    // Velocity branch — INSERT or UPDATE on velocity_overrides.
    if (parsedVelocity !== null) {
      const changed = !existingVelocity || existingVelocity.monthly_sales !== parsedVelocity;
      if (changed) {
        const existingProv = (existingVelocity?.provenance ?? {}) as Record<string, unknown>;
        const newProv = {
          ...existingProv,
          monthly_sales: { source: PROVENANCE_SOURCE_MANUAL, reason },
        };
        if (existingVelocity) {
          // U4 Slice D (R1B-F12) — direct UPDATE on velocity_overrides is REVOKEd; the edit routes
          // through the update_velocity_override CAS RPC (expected monthly_sales → STALE_CONFLICT
          // on a concurrent change, never a silent LWW). One atomic UPDATE preserves the trigger.
          const env = extractEnvelope<{ id: string }>(
            await supabase.rpc('update_velocity_override', {
              p_id: existingVelocity.id,
              p_expected_monthly_sales: existingVelocity.monthly_sales,
              p_new_monthly_sales: parsedVelocity,
              p_notes: reason,
              p_set_by: userId,
              p_provenance: newProv,
            } as never)
          );
          if (!env.isOk) {
            setSaving(false);
            const ue = env.userErrors[0];
            if (ue?.field_path != null) {
              setServerFieldErrors([ue]);
            } else {
              showToast({
                kind: 'error',
                message: `Velocity update failed — ${ue?.message ?? 'reload and retry'}`,
              });
            }
            return;
          }
        } else {
          const { error } = await supabase.from('velocity_overrides').insert({
            product_id: productId,
            // marketplace = the enum-typed stored column (value is an fpc path key).
            marketplace: path,
            monthly_sales: parsedVelocity,
            notes: reason,
            set_by: userId,
            provenance: newProv,
          } as never);
          if (error) {
            setSaving(false);
            const ue = translatePostgrestError(error);
            if (ue.field_path !== null) {
              setServerFieldErrors([ue]);
            } else {
              showToast({ kind: 'error', message: `Velocity insert failed — ${ue.message}` });
            }
            return;
          }
        }
      }
    }

    setSaving(false);
    await onSaved();
    onClose();
    showToast({ kind: 'success', message: 'Overrides saved' });
  }

  async function handleRemove(target: 'velocity' | 'price'): Promise<void> {
    const row = target === 'velocity' ? existingVelocity : existingPrice;
    if (!row) {
      setConfirmRemove(null);
      return;
    }
    setRemoving(true);
    const table = target === 'velocity' ? 'velocity_overrides' : 'price_overrides';
    const { error } = await supabase.from(table).delete().eq('id', row.id);
    setRemoving(false);
    setConfirmRemove(null);
    if (error) {
      showToast({
        kind: 'error',
        message: `Remove failed — ${translatePostgrestError(error).message}`,
      });
      return;
    }
    await onSaved();
    onClose();
    showToast({
      kind: 'success',
      message: target === 'velocity' ? 'Velocity override removed' : 'Price override removed',
    });
  }

  const busy = saving || removing;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop — keyboard dismiss handled via Escape listener above */}
        <button
          type="button"
          className="absolute inset-0 bg-black/60 cursor-default"
          onClick={busy ? undefined : onClose}
          aria-label="Close override dialog"
          tabIndex={-1}
        />
        <div
          className="relative bg-surface-2 border border-border rounded-xl w-[480px] max-h-[85vh] flex flex-col shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-label="Edit manual overrides"
        >
          <div className="px-5 py-3 border-b border-border-subtle">
            <h3 className="text-sm font-semibold text-ink-bright">Edit manual overrides</h3>
            <p className="text-2xs text-ink-muted mt-0.5">
              {pathLabel} — leave a field blank to keep the existing value or use Remove to revert
              to the engine fallback.
            </p>
          </div>

          <div className="px-5 py-4 space-y-4 overflow-y-auto">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="ovr-price"
                  className="text-2xs uppercase tracking-wider text-ink-muted"
                >
                  Sale Price
                </label>
                {existingPrice && (
                  <button
                    type="button"
                    onClick={() => setConfirmRemove('price')}
                    disabled={busy}
                    className="text-2xs text-danger hover:text-danger/80 disabled:opacity-50"
                  >
                    Remove override
                  </button>
                )}
              </div>
              <input
                ref={firstInputRef}
                id="ovr-price"
                type="number"
                step="0.01"
                min="0"
                value={salePriceInput}
                onChange={(e) => setSalePriceInput(e.target.value)}
                disabled={busy}
                placeholder={existingPrice ? '' : 'e.g. 24.99'}
                className="w-full text-sm bg-surface-3 border border-border-subtle rounded px-2 py-1.5 text-ink-bright focus:outline-none focus:border-accent disabled:opacity-50"
              />
              <FieldError fieldPath="sale_price" userErrors={serverFieldErrors} />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="ovr-vel"
                  className="text-2xs uppercase tracking-wider text-ink-muted"
                >
                  Velocity (monthly sales)
                </label>
                {existingVelocity && (
                  <button
                    type="button"
                    onClick={() => setConfirmRemove('velocity')}
                    disabled={busy}
                    className="text-2xs text-danger hover:text-danger/80 disabled:opacity-50"
                  >
                    Remove override
                  </button>
                )}
              </div>
              <input
                id="ovr-vel"
                type="number"
                step="1"
                min="0"
                value={velocityInput}
                onChange={(e) => setVelocityInput(e.target.value)}
                disabled={busy}
                placeholder={existingVelocity ? '' : 'e.g. 12'}
                className="w-full text-sm bg-surface-3 border border-border-subtle rounded px-2 py-1.5 text-ink-bright focus:outline-none focus:border-accent disabled:opacity-50"
              />
              <FieldError fieldPath="monthly_sales" userErrors={serverFieldErrors} />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="ovr-reason"
                className="text-2xs uppercase tracking-wider text-ink-muted"
              >
                Reason (optional)
              </label>
              <textarea
                id="ovr-reason"
                rows={2}
                value={reasonInput}
                onChange={(e) => setReasonInput(e.target.value)}
                disabled={busy}
                placeholder="Why this override is being set"
                className="w-full text-xs bg-surface-3 border border-border-subtle rounded px-2 py-1.5 text-ink-bright focus:outline-none focus:border-accent disabled:opacity-50 resize-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 px-5 py-3 border-t border-border-subtle">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright px-3 py-1.5 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="arbi-btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemove !== null}
        title={
          confirmRemove === 'velocity' ? 'Remove velocity override?' : 'Remove price override?'
        }
        message={
          confirmRemove === 'velocity'
            ? 'The engine will fall back to the Algopix monthly sales estimate.'
            : 'The engine will fall back to the per-path sale price chain.'
        }
        confirmLabel={removing ? 'Removing...' : 'Remove'}
        onCancel={() => (removing ? undefined : setConfirmRemove(null))}
        onConfirm={() => {
          if (confirmRemove) void handleRemove(confirmRemove);
        }}
      />
    </>
  );
}
